// @flow

import * as React from 'react';

import { useField } from 'react-final-form';

import { selectTaxesDisplaySetting } from '@gandi/authentication';
import {
  ChoiceField,
  ChoiceFieldGroup,
  useBasicValidators,
} from '@gandi/form/FinalForm';
import { useTranslator } from '@gandi/translate';
import { Base, Label, Placeholder, SrOnly, TextPlaceholder } from '@gandi/ui';

import { useAppSelector } from 'src/hooks';
import Price from 'src/shared/Price';

import { MAILBOX_DURATION_ONE_MONTH } from '../../../../MailboxCreate/consts';
import { useDurationToLabel } from '../../../modules/hooks/useDurationToLabel';
import { useProductsFetch } from '../../../modules/hooks/useProductsFetch';

const PRODUCTS_PLACEHOLDERS = [{ min_duration: 1 }, { min_duration: 12 }];

const PRICES_PLACEHOLDERS = [
  {
    price_after_taxes: 0,
    price_before_taxes: 0,
    min_duration: 1,
  },
  {
    price_after_taxes: 0,
    price_before_taxes: 0,
    min_duration: 12,
  },
];

export function DurationField(): React.MixedElement {
  const { __ } = useTranslator();
  const { required } = useBasicValidators();

  const {
    input: { value: configName },
    // $FlowFixMe[prop-missing]
  } = useField('config_name', {
    subscription: { value: true },
  });

  const {
    input: { value: durationMonth },
    // $FlowFixMe[prop-missing]
  } = useField('duration_month', {
    subscription: { value: true },
  });

  const { summaries } = useProductsFetch();
  const selectedOffer = summaries?.find(
    product => product.config_name === configName,
  );

  const prices = !summaries
    ? PRODUCTS_PLACEHOLDERS
    : selectedOffer?.prices || PRICES_PLACEHOLDERS;

  const { durationToBillingCycle, durationToPeriodicity } =
    useDurationToLabel();

  const hasTaxes = selectedOffer?.taxes.length
    ? selectedOffer.taxes[0].rate !== 0
    : false;
  const displayTaxes = useAppSelector(selectTaxesDisplaySetting);

  /**
   * We reverse the prices because the data received from the API is not
   * in the same order for both standard and premium.
   *
   * Hence sice the key passed to react is the min duration, react based on
   * the offer we choose invert the order of the prices (billing cycle).
   */
  const needToReversePrices = prices.length > 0 && prices[0].min_duration !== 1;

  return (
    <>
      {
        // $FlowFixMe[prop-missing]
        <Label aria-hidden>{__('Select your billing cycle')}</Label>
      }
      <ChoiceFieldGroup
        choiceGroupProps={{
          legend: __('Select your billing cycle'),
          layout: 'row',
        }}
        name="duration_month"
        validate={required}
      >
        {(needToReversePrices ? prices.reverse() : prices).map(price => {
          const amount = displayTaxes
            ? price.price_after_taxes
            : price.price_before_taxes;
          return (
            <ChoiceField
              key={price.min_duration}
              name="duration_month"
              value={price.min_duration.toString()}
            >
              <Base slot="label" weight="semibold">
                {!Number.isFinite(amount) ? (
                  <>
                    <SrOnly>{__('Loading offers...')}</SrOnly>
                    <Placeholder display="inline-block" chars={15} />
                  </>
                ) : (
                  durationToBillingCycle(price.min_duration)
                )}
              </Base>
              <Base slot="additional">
                {!Number.isFinite(amount) ? (
                  <Placeholder display="inline-block" chars={10} />
                ) : (
                  <>
                    {!amount ? (
                      <TextPlaceholder />
                    ) : (
                      <Price
                        currency={
                          // $FlowFixMe[incompatible-type]
                          price.currency
                        }
                        inline
                        price={amount * price.min_duration}
                        priceInfo={` ${durationToPeriodicity(
                          price.min_duration,
                        )}`}
                        showExcludeVatMention={hasTaxes && !displayTaxes}
                      />
                    )}
                  </>
                )}
              </Base>
            </ChoiceField>
          );
        })}
      </ChoiceFieldGroup>
      {durationMonth && (
        <Base tagName="p" marginTop="small" marginBottom="none">
          {durationMonth === MAILBOX_DURATION_ONE_MONTH
            ? __('Pay each month. Saving a credit card is mandatory.')
            : __('Pay each year. No specific payment method required.')}{' '}
          {__('Cancel the subscription whenever you want.')}
        </Base>
      )}
    </>
  );
}
